import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { Redirect, useParams } from "react-router-dom";
import Iframe from "react-iframe";
import {
  FaIdCard,
  FaUsers,
  FaCog,
  FaImage,
  FaPalette,
  FaEye,
  FaCheck,
  FaUserGraduate,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlStudentIdCardSettings from "../dialog/MdlStudentIdCardSettings";
import MdlSelectMultipleStudentMultipleClass from "../dialog/MdlSelectMultipleStudentMultipleClass";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SYSTEM_CODE = process.env.REACT_APP_SYSTEM_CODE;
const SERVER_NAME = process.env.REACT_APP_SERVER_NAME;
const SELECTION_TYPE = "student";
const PREVIEW_FLAG = "1";
const DEFAULT_IFRAME_HEIGHT = "300px";

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_STYLE = {
  background: "#ffffff",
  backgroundColor: "#ffffff",
  backgroundImage: "none",
  fontFamily:
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

const SHELL_STYLE = { "--mMargin": "0px", background: "#ffffff" };
const IFRAME_STYLE = { border: "none", overflow: "hidden" };

//Created By Cursor
function parseColorPair(code) {
  const parts = String(code || "").split("_");
  const color1 = parts[0] || "#cbd5e1";
  return { color1, color2: parts[1] || color1 };
}

//Created By Cursor
function buildIdCardUrls({
  fileDirectory,
  fileName,
  students,
  sampleId,
  colorOptionId,
  refreshKey,
}) {
  if (!fileDirectory || !fileName) {
    return { fileUrl: "", previewUrl: "" };
  }

  const studentIds =
    students.length > 0
      ? students.map((s) => s.student_enrollment_no)
      : [];
  const sessionId = students[0]?.session_id || "";
  const schoolCode = SessionManager.getSchoolCode();

  const qs =
    `?&student_ids=${JSON.stringify(studentIds)}` +
    `&class_id=&section_id=&session_id=${sessionId}` +
    `&sample_id=${sampleId}&selection_type=${SELECTION_TYPE}` +
    `&system_code=${SYSTEM_CODE}&color_option_id=${colorOptionId}` +
    `&school_code=${schoolCode}&rk=${refreshKey}`;

  const base = `${SERVER_NAME}${fileDirectory}${fileName}`;
  const fileUrl = base + qs;
  const previewUrl = `${base}${qs}&preview=${PREVIEW_FLAG}`;
  console.log("Full URL:", fileUrl);
  console.log("Preview URL:", previewUrl);
  return { fileUrl, previewUrl };
}

const SampleCard = memo(function SampleCard({
  item,
  index,
  active,
  total,
  onSelect,
}) {
  return (
    <button
      type="button"
      className={`pdid-sample${active ? " is-active" : ""}`}
      onClick={() => onSelect(index)}
      role="listitem"
      aria-pressed={active}
      style={{ "--pdid-i": index }}
    >
      <span className="pdid-sample__badge">
        {active ? (
          <>
            <FaCheck aria-hidden /> Selected
          </>
        ) : (
          <>Design {index + 1}</>
        )}
      </span>
      <span className="pdid-sample__img" aria-hidden>
        <img
          src={item.sample_img}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </span>
      <span className="pdid-sample__meta">
        <span className="pdid-sample__name">
          {item.sample_name || `Design ${index + 1}`}
        </span>
        <span className="pdid-sample__step">
          {index + 1}/{total}
        </span>
      </span>
    </button>
  );
});

const ColorCard = memo(function ColorCard({ item, index, active, onSelect }) {
  const { color1, color2 } = parseColorPair(item.sample_color_code);

  return (
    <button
      type="button"
      className={`pdid-color${active ? " is-active" : ""}`}
      onClick={() => onSelect(index)}
      title={item.sample_color_name}
      role="listitem"
      style={{ "--pdid-i": index }}
    >
      {active ? (
        <span className="pdid-color__check" aria-hidden>
          <FaCheck />
        </span>
      ) : null}
      <span className="pdid-color__swatch" aria-hidden>
        <span style={{ backgroundColor: color1 }} />
        <span style={{ backgroundColor: color2 }} />
      </span>
      <span className="pdid-color__name">
        {item.sample_color_name || "Colour"}
      </span>
    </button>
  );
});

const PrintableDocsIdCard = () => {
  const { document_type } = useParams();
  const pageRef = useRef(null);

  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(false);
  const [isOpenSelectStudent, setIsOpenSelectStudent] = useState(false);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [students, setStudents] = useState([]);
  const [samples, setSamples] = useState([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [height, setHeight] = useState(DEFAULT_IFRAME_HEIGHT);
  const [refreshKey, setRefreshKey] = useState(0);

  const sample = samples[sampleIndex] || null;
  const colorOptions = sample?.color_options || [];
  const colorOption = colorOptions[colorIndex] || null;

  const documentCaption =
    samples[0]?.document_caption || "ID Card";
  const selectedSampleName = sample?.sample_name || "";
  const selectedColorName = colorOption?.sample_color_name || "";
  const sampleId = sample?.sample_id || "";
  const colorOptionId = colorOption?.color_option_id || "";
  const studentCount = students.length;

  const { fileUrl, previewUrl } = useMemo(
    () =>
      buildIdCardUrls({
        fileDirectory: sample?.file_directory || "",
        fileName: sample?.file_name || "",
        students,
        sampleId,
        colorOptionId,
        refreshKey,
      }),
    [sample, students, sampleId, colorOptionId, refreshKey]
  );

  //Created By Cursor
  const selectSample = useCallback((index) => {
    setSampleIndex(index);
    setColorIndex(0);
  }, []);

  //Created By Cursor
  const selectColor = useCallback((index) => {
    setColorIndex(index);
  }, []);

  //Created By Cursor
  const handleSelectedStudent = useCallback((list) => {
    setIsOpenSelectStudent(false);
    setStudents(list || []);
  }, []);

  //Created By Cursor
  const closeSelectStudent = useCallback(() => {
    setIsOpenSelectStudent(false);
  }, []);

  //Created By Cursor
  const openSelectStudent = useCallback(() => {
    setIsOpenSelectStudent(true);
  }, []);

  //Created By Cursor
  const closeSettings = useCallback(() => {
    setIsOpenSettings(false);
  }, []);

  //Created By Cursor
  const openSettings = useCallback(() => {
    if (!sampleId) {
      myToast.notify_warning("Select a sample first");
      return;
    }
    if (!studentCount) {
      myToast.notify_warning("Select students first");
      return;
    }
    setIsOpenSettings(true);
  }, [sampleId, studentCount]);

  //Created By Cursor
  const handleSettingsDone = useCallback(() => {
    setIsOpenSettings(false);
    setRefreshKey((k) => k + 1);
  }, []);

  //Created By Cursor
  const hidePreviewLoader = useCallback(() => {
    setShowPreview(false);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    const el = pageRef.current;
    if (el) {
      el.style.setProperty("background", "#ffffff", "important");
      el.style.setProperty("background-color", "#ffffff", "important");
      el.style.setProperty("background-image", "none", "important");
    }
    document.documentElement.classList.add("pdid-page-active");
    document.body.classList.add("pdid-page-active");

    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);

    return () => {
      document.documentElement.classList.remove("pdid-page-active");
      document.body.classList.remove("pdid-page-active");
      window.removeEventListener("message", onMessage);
    };
  }, []);

  useEffect(() => {
    if (!loggedIn || !document_type) return undefined;

    let active = true;
    const source = axios.CancelToken.source();
    setLoading(true);

    axios
      .get(`${API_BASE}getPrintableSample.php`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          document_type,
        },
        cancelToken: source.token,
      })
      .then((res) => res.data)
      .then((response) => {
        if (!active) return;
        if (response.api_status === "OK" && Array.isArray(response.data)) {
          setSamples(response.data);
          setSampleIndex(0);
          setColorIndex(0);
        } else {
          setSamples([]);
          myToast.notify_warning(response.api_remark || "No samples found");
        }
      })
      .catch((error) => {
        if (!active || axios.isCancel(error)) return;
        console.error("[PrintableDocsIdCard] samples error", error);
        myToast.notify_warning("Failed to load printable samples.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      source.cancel("unmount");
    };
  }, [document_type, loggedIn]);

  useEffect(() => {
    if (!fileUrl) return;
    setHeight(DEFAULT_IFRAME_HEIGHT);
    setShowPreview(true);
  }, [fileUrl]);

  const heroSub = useMemo(() => {
    if (studentCount > 0) {
      return `${studentCount} student${
        studentCount === 1 ? "" : "s"
      } selected · pick sample & colour`;
    }
    return "Select students, choose a sample & colour, then preview";
  }, [studentCount]);

  const previewSub = useMemo(() => {
    if (studentCount > 0) {
      return `Showing cards for ${studentCount} student${
        studentCount === 1 ? "" : "s"
      }`;
    }
    return "Select students to generate preview";
  }, [studentCount]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div ref={pageRef} className="content payroll-page sm-page sr-page pdid-page" style={PAGE_STYLE}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Printable · {documentCaption}
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell"
        style={SHELL_STYLE}
      >
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaIdCard />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{documentCaption}</h1>
              <p className="sm-hero__sub">{heroSub}</p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {studentCount > 0 ? (
              <span className="pdid-chip">
                <FaUserGraduate aria-hidden />
                {studentCount} selected
              </span>
            ) : null}
            <button
              type="button"
              className="pdid-hero-btn pdid-hero-btn--solid"
              onClick={openSelectStudent}
            >
              <FaUsers aria-hidden />
              <span>
                {studentCount ? "Change Students" : "Select Students"}
              </span>
            </button>
            <button
              type="button"
              className="pdid-hero-btn"
              onClick={openSettings}
            >
              <FaCog aria-hidden />
              <span>Settings</span>
            </button>
          </div>
        </header>

        {loading ? (
          <div className="pdid-loading">
            <MyLoader />
          </div>
        ) : (
          <div className="sr-body pdid-body">
            {selectedSampleName || selectedColorName ? (
              <div className="pdid-status" aria-live="polite">
                {selectedSampleName ? (
                  <span className="pdid-status__item">
                    <FaImage aria-hidden />
                    {selectedSampleName}
                  </span>
                ) : null}
                {selectedColorName ? (
                  <span className="pdid-status__item">
                    <FaPalette aria-hidden />
                    {selectedColorName}
                  </span>
                ) : null}
              </div>
            ) : null}

            <section className="pdid-panel pdid-panel--sample pdid-panel--enter">
              <div className="pdid-panel__head pdid-sample-head">
                <div className="pdid-sample-head__brand">
                  <div className="pdid-panel__icon" aria-hidden>
                    <FaIdCard />
                  </div>
                  <div className="min-w-0">
                    <h2 className="pdid-panel__title">Sample designs</h2>
                    <p className="pdid-panel__sub">
                      {samples.length
                        ? "Tap a card to use it for the ID preview"
                        : "No samples found"}
                    </p>
                  </div>
                </div>
                {samples.length > 0 ? (
                  <div className="pdid-sample-head__aside">
                    <span className="pdid-sample-count">
                      <strong>{samples.length}</strong>
                      <span>
                        design{samples.length === 1 ? "" : "s"}
                      </span>
                    </span>
                    {selectedSampleName ? (
                      <span className="pdid-sample-current">
                        Using · {selectedSampleName}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="pdid-panel__body pdid-sample-body">
                {samples.length === 0 ? (
                  <div className="pdid-empty">
                    <FaImage className="pdid-empty__icon" aria-hidden />
                    <p>No printable samples found</p>
                  </div>
                ) : (
                  <div className="pdid-samples" role="list">
                    {samples.map((item, index) => (
                      <SampleCard
                        key={item.sample_id || index}
                        item={item}
                        index={index}
                        total={samples.length}
                        active={sampleIndex === index}
                        onSelect={selectSample}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section
              className="pdid-panel pdid-panel--enter"
              style={{ "--pdid-delay": "60ms" }}
            >
              <div className="pdid-panel__head">
                <div
                  className="pdid-panel__icon pdid-panel__icon--mint"
                  aria-hidden
                >
                  <FaPalette />
                </div>
                <div className="min-w-0">
                  <h2 className="pdid-panel__title">Colour</h2>
                  <p className="pdid-panel__sub">
                    {colorOptions.length
                      ? "Pick a colour pair for this sample"
                      : "No colour options for this sample"}
                  </p>
                </div>
              </div>
              <div className="pdid-panel__body">
                {colorOptions.length === 0 ? (
                  <div className="pdid-empty pdid-empty--compact">
                    No colour options
                  </div>
                ) : (
                  <div className="pdid-colors" role="list">
                    {colorOptions.map((item, index) => (
                      <ColorCard
                        key={item.color_option_id || index}
                        item={item}
                        index={index}
                        active={colorIndex === index}
                        onSelect={selectColor}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section
              className="pdid-panel pdid-panel--preview pdid-panel--enter"
              style={{ "--pdid-delay": "120ms" }}
            >
              <div className="pdid-panel__head">
                <div
                  className="pdid-panel__icon pdid-panel__icon--sky"
                  aria-hidden
                >
                  <FaEye />
                </div>
                <div className="min-w-0">
                  <h2 className="pdid-panel__title">Preview</h2>
                  <p className="pdid-panel__sub">{previewSub}</p>
                </div>
              </div>
              <div className="pdid-panel__body">
                <div className="pdid-preview">
                  {showPreview ? (
                    <div className="pdid-preview__loader">
                      <MyLoader />
                    </div>
                  ) : null}
                  {fileUrl ? (
                    <Iframe
                      src={fileUrl}
                      width="100%"
                      height={height}
                      style={IFRAME_STYLE}
                      id="iFrame1"
                      scrolling="no"
                      onLoad={hidePreviewLoader}
                    />
                  ) : (
                    <div className="pdid-empty">
                      <FaIdCard className="pdid-empty__icon" aria-hidden />
                      <p>Choose a sample to load preview</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>

      {isOpenSelectStudent ? (
        <MdlSelectMultipleStudentMultipleClass
          actionClose={closeSelectStudent}
          actionHandleSelected={handleSelectedStudent}
        />
      ) : null}

      {isOpenSettings ? (
        <MdlStudentIdCardSettings
          sample_id={sampleId}
          color_option_id={colorOptionId}
          actionClose={closeSettings}
          actionDone={handleSettingsDone}
          file_url={previewUrl}
        />
      ) : null}
    </div>
  );
};

export default PrintableDocsIdCard;
